import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCampaign } from "@/contexts/CampaignContext";
import { PageContainer } from "@/components/PageContainer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
	CardFooter,
} from "@/components/ui/card";
import { useToast } from "@/hooks/useToast";
import { fetchZipCodesInRadius } from "@/api/zipCodes";
import { getAudienceSegments } from "@/api/segments";
import { getAudienceMetrics } from "@/api/audienceMetrics";
import type { AudienceSegment } from "@/api/segments";

interface AudienceMetrics {
	totalAudience: number;
	totalEmails: number;
	totalPhones: number;
	totalDevices: number;
	totalIPs: number;
}

interface FormData {
	zipCode: string;
	radius: number;
	zipCodes: string;
	selectedSegments: string[];
}

export function AudienceTargeting() {
	const navigate = useNavigate();
	const { toast } = useToast();
	const { audienceTargeting, setAudienceTargeting } = useCampaign();
	const [fetchedZipCodes, setFetchedZipCodes] = useState<string[]>([]);
	const [segments, setSegments] = useState<AudienceSegment[]>([]);
	const [isLoadingSegments, setIsLoadingSegments] = useState(false);
	const [useZipRadius, setUseZipRadius] = useState(true);
	const [selectedSegments, setSelectedSegments] = useState<AudienceSegment[]>(
		[]
	);
	const [availableSegments, setAvailableSegments] = useState<AudienceSegment[]>(
		[]
	);
	const [searchFilter, setSearchFilter] = useState("");
	const [audienceMetrics, setAudienceMetrics] = useState<AudienceMetrics>();

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm({
		defaultValues: audienceTargeting || {
			zipCode: "",
			radius: 10,
			zipCodes: [],
			selectedSegments: [],
		},
	});

	useEffect(() => {
		const loadSegments = async () => {
			setIsLoadingSegments(true);
			try {
				const { segments: loadedSegments } = await getAudienceSegments();
				setAvailableSegments(loadedSegments);
				setSegments(loadedSegments);
			} catch (error) {
				toast({
					variant: "destructive",
					title: "Error",
					description: error.message,
				});
			} finally {
				setIsLoadingSegments(false);
			}
		};

		loadSegments();
	}, []);

	const onFetchZipCodes = async (zipCode: string, radius: number) => {
		if (!zipCode || !radius) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Both ZIP code and radius are required",
			});
			return;
		}

		try {
			const response = await fetchZipCodesInRadius(zipCode, radius);
			setFetchedZipCodes(response.zipCodes);
			toast({
				title: "Success",
				description: `Found ${response.zipCodes.length} ZIP codes in range`,
			});
		} catch (error: unknown) {
			toast({
				variant: "destructive",
				title: "Error",
				description:
					error instanceof Error ? error.message : "Failed to fetch ZIP codes",
			});
		}
	};

	const handleSegmentSelect = (segment: AudienceSegment) => {
		setSelectedSegments([...selectedSegments, segment]);
		setAvailableSegments(availableSegments.filter((s) => s.ID !== segment.ID));
	};

	const handleSegmentRemove = (segment: AudienceSegment) => {
		setSelectedSegments(selectedSegments.filter((s) => s.ID !== segment.ID));
		setAvailableSegments([...availableSegments, segment]);
	};

	const updateAudience = async () => {
		try {
			console.log("Selected segments before update:", selectedSegments);
			const segmentIds = selectedSegments.map((s) => s.ID);
			console.log("Segment IDs being sent:", segmentIds);

			// Get ZIP codes from either the radius search or manual entry
			const zipCodes = useZipRadius
				? fetchedZipCodes
				: (watch("zipCodes") as string)
						.split(",")
						.map((zip: string) => zip.trim());
			console.log("ZIP codes being sent:", zipCodes);

			const metrics = await getAudienceMetrics(segmentIds, zipCodes);
			console.log("Metrics response:", metrics);

			setAudienceMetrics({
				totalAudience: metrics.totalCount,
				totalEmails: metrics.emailCount,
				totalPhones: metrics.phoneCount,
				totalDevices: metrics.deviceCount,
				totalIPs: metrics.iPCount,
			});

			toast({
				title: "Success",
				description: "Audience metrics updated successfully",
			});
		} catch (error: unknown) {
			console.error("Error in updateAudience:", error);
			toast({
				variant: "destructive",
				title: "Error",
				description:
					error instanceof Error
						? error.message
						: "Failed to update audience metrics",
			});
		}
	};

	const onSubmit = (data: FormData) => {
		const zipCodesArray = useZipRadius
			? fetchedZipCodes
			: data.zipCodes.split(",").map((zip: string) => zip.trim());

		setAudienceTargeting({
			...data,
			zipCodes: zipCodesArray,
			selectedSegments: selectedSegments.map((s) => s.ID),
			audienceMetrics,
			segmentLabels: selectedSegments.map((s) => s.Label),
		});
		navigate("/create/creative");
	};

	const filteredSegments = availableSegments.filter(
		(segment) =>
			segment.Label.toLowerCase().includes(searchFilter.toLowerCase()) ||
			segment.ID.toLowerCase().includes(searchFilter.toLowerCase())
	);

	return (
		<PageContainer>
			<form onSubmit={handleSubmit(onSubmit)}>
				<CardHeader>
					<CardTitle>Audience Targeting</CardTitle>
					<CardDescription>
						Define your target audience location
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					<div>
						<h3 className="text-lg font-semibold mb-4">
							Step 1: Generate ZIP Codes
						</h3>
						<div className="flex items-center space-x-4 mb-4">
							<Button
								type="button"
								variant={useZipRadius ? "default" : "outline"}
								onClick={() => setUseZipRadius(true)}>
								ZIP + Radius
							</Button>
							<Button
								type="button"
								variant={!useZipRadius ? "default" : "outline"}
								onClick={() => setUseZipRadius(false)}>
								Manual Entry
							</Button>
						</div>

						{useZipRadius && (
							<>
								<div className="grid grid-cols-2 gap-6">
									<div className="space-y-2">
										<Label htmlFor="zipCode">ZIP Code *</Label>
										<Input
											{...register("zipCode", {
												required: "ZIP code is required",
												pattern: {
													value: /^\d{5}$/,
													message: "Please enter a valid 5-digit ZIP code",
												},
											})}
										/>
										{errors.zipCode && (
											<p className="text-sm text-destructive mt-1">
												{errors.zipCode.message}
											</p>
										)}
									</div>
									<div className="space-y-2">
										<Label htmlFor="radius">Radius (miles) *</Label>
										<Input
											type="number"
											{...register("radius", {
												required: "Radius is required",
												min: {
													value: 1,
													message: "Radius must be at least 1 mile",
												},
												max: {
													value: 100,
													message: "Radius cannot exceed 100 miles",
												},
											})}
										/>
										{errors.radius && (
											<p className="text-sm text-destructive mt-1">
												{errors.radius.message}
											</p>
										)}
									</div>
								</div>

								<Button
									type="button"
									variant="secondary"
									onClick={() =>
										onFetchZipCodes(watch("zipCode"), watch("radius"))
									}>
									Generate ZIP Codes
								</Button>

								{fetchedZipCodes.length > 0 && (
									<div className="border rounded-lg p-4">
										<p className="font-medium text-gray-900">
											Found ZIP Codes:
										</p>
										<p className="mt-2 text-gray-600">
											{fetchedZipCodes.join(", ")}
										</p>
									</div>
								)}
							</>
						)}

						{!useZipRadius && (
							<div className="space-y-2">
								<Label htmlFor="zipCodes">Manual ZIP Codes Entry</Label>
								<Textarea
									{...register("zipCodes")}
									placeholder="Enter ZIP codes separated by commas"
								/>
							</div>
						)}
					</div>

					{isLoadingSegments ? (
						<p>Loading segments...</p>
					) : (
						<>
							<div>
								<h3 className="text-lg font-semibold mb-4">
									Step 2: Select Segments
								</h3>
								<div className="space-y-2">
									<Label>Available Segments</Label>
									<Input
										type="search"
										placeholder="Search segments..."
										value={searchFilter}
										onChange={(e) => setSearchFilter(e.target.value)}
										className="mb-2"
									/>
									<div className="border rounded-md p-4 h-[300px] overflow-y-auto">
										{filteredSegments.map((segment) => (
											<div key={segment.ID} className="mb-2">
												<Button
													type="button"
													variant="outline"
													className="w-full text-left justify-start"
													onClick={() => handleSegmentSelect(segment)}>
													{segment.Label} ({segment.ID})
												</Button>
											</div>
										))}
									</div>
								</div>

								<div className="space-y-2">
									<Label>Selected Segments</Label>
									<div className="border rounded-md p-4">
										{selectedSegments.map((segment) => (
											<Button
												key={segment.ID}
												type="button"
												variant="secondary"
												className="m-1 justify-start"
												onClick={() => handleSegmentRemove(segment)}>
												{segment.Label} ({segment.ID}) ✕
											</Button>
										))}
									</div>
								</div>
							</div>

							<div className="border rounded-lg p-4">
								<h3 className="text-lg font-semibold mb-4">
									Step 3: Build Audience
								</h3>
								<div className="grid grid-cols-5 gap-4 mb-4">
									{audienceMetrics && (
										<>
											<div className="bg-secondary p-4 rounded-lg shadow">
												<h4 className="font-medium text-sm text-gray-300">
													Total Audience
												</h4>
												<p className="text-2xl font-bold text-white">
													{audienceMetrics.totalAudience.toLocaleString()}
												</p>
											</div>
											<div className="bg-secondary p-4 rounded-lg shadow">
												<h4 className="font-medium text-sm text-gray-300">
													Total Emails
												</h4>
												<p className="text-2xl font-bold text-white">
													{audienceMetrics.totalEmails.toLocaleString()}
												</p>
											</div>
											<div className="bg-secondary p-4 rounded-lg shadow">
												<h4 className="font-medium text-sm text-gray-300">
													Total Phones
												</h4>
												<p className="text-2xl font-bold text-white">
													{audienceMetrics.totalPhones.toLocaleString()}
												</p>
											</div>
											<div className="bg-secondary p-4 rounded-lg shadow">
												<h4 className="font-medium text-sm text-gray-300">
													Total Devices
												</h4>
												<p className="text-2xl font-bold text-white">
													{audienceMetrics.totalDevices.toLocaleString()}
												</p>
											</div>
											<div className="bg-secondary p-4 rounded-lg shadow">
												<h4 className="font-medium text-sm text-gray-300">
													Total IPs
												</h4>
												<p className="text-2xl font-bold text-white">
													{audienceMetrics.totalIPs.toLocaleString()}
												</p>
											</div>
										</>
									)}
								</div>
								<div className="flex items-center gap-4">
									<Button
										type="button"
										variant="secondary"
										size="lg"
										className={`text-xl font-bold px-10 py-7 bg-secondary hover:bg-secondary/90 text-white relative ${
											!audienceMetrics
												? "before:absolute before:inset-0 before:rounded-lg before:border-2 before:border-white/50 before:animate-[ping_3s_cubic-bezier(0,0,0.2,1)_infinite]"
												: ""
										}`}
										onClick={updateAudience}>
										<span className="relative z-10">Build Audience</span>
									</Button>
									<p className="text-gray-600 text-sm max-w-md">
										You must build the audience before proceeding. If you make
										any changes to segments or locations, please rebuild the
										audience.
									</p>
								</div>
							</div>
						</>
					)}
				</CardContent>
				<CardFooter className="flex justify-between">
					<Button
						type="button"
						variant="outline"
						onClick={() => navigate("/create")}>
						Back
					</Button>
					<Button
						type="submit"
						disabled={
							(!fetchedZipCodes.length && !watch("zipCodes")) ||
							!selectedSegments.length ||
							!audienceMetrics
						}>
						Approve Audience & Upload Creative
					</Button>
				</CardFooter>
			</form>
		</PageContainer>
	);
}
